import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Form, Input, Button, Typography, Card, message, Space, Divider, Tabs, Checkbox } from 'antd';
import { LockOutlined, UserOutlined, MailOutlined, PhoneOutlined, BankOutlined, HomeOutlined, NumberOutlined } from '@ant-design/icons';
import { supabase } from '../lib/supabase';
import Logo from './Logo';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

export default function SetPassword() {
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [hasSession, setHasSession] = useState(false);
  const [userMetadata, setUserMetadata] = useState<any>({});
  const [userId, setUserId] = useState<string | null>(null);
  const [emailFromUrl, setEmailFromUrl] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('1');
  const [sameAsContact, setSameAsContact] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();

  useEffect(() => {
    const checkSessionAndParams = async () => {
      try {
        setInitialLoading(true);
        
        // Extract all possible parameters from URL
        console.log('Full URL:', window.location.href);
        
        // Parse hash parameters
        const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
        console.log('Hash params:', Object.fromEntries(hashParams.entries()));
        
        // Parse query parameters
        console.log('Query params:', Object.fromEntries(searchParams.entries()));
        
        // Check if this is from an invite link redirect via auth/callback
        const fromInvite = searchParams.get('fromInvite') === 'true';
        const fromSession = searchParams.get('fromSession') === 'true';
        
        console.log('Invite flow detection:', { fromInvite, fromSession });
        
        // Look for fullHash parameter which contains the original hash from the auth callback
        const fullHashParam = searchParams.get('fullHash');
        if (fullHashParam) {
          console.log('Found fullHash parameter from auth callback');
        }
        
        // Extract email from URL if present
        // The email might be in various parameters, check common ones
        const email = searchParams.get('email') || 
                      searchParams.get('user_email') || 
                      searchParams.get('userEmail') ||
                      hashParams.get('email');
                      
        if (email) {
          console.log('Email found in URL:', email);
          setEmailFromUrl(email);
          form.setFieldsValue({ email });
        }
                
        // Get the session
        const { data, error } = await supabase.auth.getSession();
        
        // Check if we can get metadata directly from Supabase without session
        // This applies when the user clicks a magic link
        let metadataFromUser = null;
        
        // Try to extract token from URL or hash
        const token = searchParams.get('token') || 
                      searchParams.get('access_token') || 
                      hashParams.get('access_token');
                      
        // Check tokens from the auth callback redirect
        const callbackToken = searchParams.get('access_token');
        if (callbackToken) {
          console.log('Found access_token from auth callback redirect');
        }
        
        // Get refresh token parameter if passed from callback
        const refreshToken = searchParams.get('refresh_token');
                      
        // Try to directly get the hash value as a possible token
        const fullHash = fullHashParam || window.location.hash.replace('#', '');
        
        // First attempt: Try to use session if available
        if (!error && data?.session?.user) {
          console.log('Active session found, using its metadata');
          setHasSession(true);
          setUserId(data.session.user.id);
          metadataFromUser = data.session.user.user_metadata || {};
          
          // Also ensure email is set
          if (!email && data.session.user.email) {
            setEmailFromUrl(data.session.user.email);
            form.setFieldsValue({ email: data.session.user.email });
          }
        }
        // Second attempt: Try with explicit tokens from various sources
        else if (token || callbackToken || fullHashParam || refreshToken) {
          console.log('No active session, but found tokens to try');
          
          try {
            // First try with the explicit access token (highest chance of success)
            const tokenToUse = callbackToken || token;
            if (tokenToUse) {
              console.log('Trying explicit access token to get user metadata');
              const { data: userData } = await supabase.auth.getUser(tokenToUse);
              if (userData?.user) {
                console.log('Got user data from explicit token');
                metadataFromUser = userData.user.user_metadata || {};
                setUserId(userData.user.id);
                // Also set email if it's not already set
                if (!email && userData.user.email) {
                  setEmailFromUrl(userData.user.email);
                  form.setFieldsValue({ email: userData.user.email });
                }
              }
            }
            
            // If that didn't work and we have a refresh token, try using it to get a session
            if (!metadataFromUser && refreshToken) {
              console.log('Trying to use refresh token to establish session');
              const { data: sessionData, error: sessionError } = await supabase.auth.refreshSession({
                refresh_token: refreshToken
              });
              
              if (!sessionError && sessionData?.user) {
                console.log('Got user data from refresh token');
                metadataFromUser = sessionData.user.user_metadata || {};
                setUserId(sessionData.user.id);
                setHasSession(true);
                // Also set email if it's not already set
                if (!email && sessionData.user.email) {
                  setEmailFromUrl(sessionData.user.email);
                  form.setFieldsValue({ email: sessionData.user.email });
                }
              }
            }
            
            // If we still don't have metadata, try the full hash as a last resort
            if (!metadataFromUser && fullHash) {
              console.log('Trying full hash as token:', fullHash.substring(0, 10) + '...');
              const { data: hashUserData } = await supabase.auth.getUser(fullHash);
              if (hashUserData?.user) {
                console.log('Got user data from hash token');
                metadataFromUser = hashUserData.user.user_metadata || {};
                setUserId(hashUserData.user.id);
                // Also set email if it's not already set
                if (!email && hashUserData.user.email) {
                  setEmailFromUrl(hashUserData.user.email);
                  form.setFieldsValue({ email: hashUserData.user.email });
                }
              }
            }
          } catch (tokenError) {
            console.error('Error trying to get user from token:', tokenError);
          }
        }
        
        // If we have an error or no session, and no metadata yet, try with email signin as last resort
        if (!metadataFromUser && email) {
          console.log('No metadata found yet, trying one more approach with email');
          
          try {
            // Try getting user info by email directly from Supabase
            const { data: userListData, error: userListError } = await supabase
              .from('profiles')
              .select('*')
              .eq('email', email)
              .maybeSingle();
            
            if (!userListError && userListData) {
              console.log('Found user profile data from database lookup:', userListData);
              
              // Use database record directly for form prefill
              form.setFieldsValue({
                voornaam: userListData.voornaam || '',
                achternaam: userListData.achternaam || '',
                telefoon: userListData.telefoon || ''
              });
              
              // Also try looking up company data
              if (userListData.id) {
                const { data: companyData, error: companyError } = await supabase
                  .from('companies')
                  .select('*')
                  .eq('user_id', userListData.id)
                  .maybeSingle();
                
                if (!companyError && companyData) {
                  console.log('Found company data from database lookup:', companyData);
                  form.setFieldsValue({
                    bedrijfsnaam: companyData.bedrijfsnaam || '',
                    kvk_nummer: companyData.kvk_nummer || '',
                    btw_nummer: companyData.btw_nummer || '',
                    adres: companyData.adres || '',
                    postcode: companyData.postcode || '',
                    stad: companyData.stad || '',
                    land: companyData.land || 'Nederland',
                    // Financial contact info
                    voornaam_financieel: companyData.voornaam_financieel || '',
                    achternaam_financieel: companyData.achternaam_financieel || '',
                    telefoon_financieel: companyData.telefoon_financieel || '',
                    email_financieel: companyData.email_financieel || ''
                  });
                }
              }
            }
          } catch (dbLookupError) {
            console.error('Error looking up user in database:', dbLookupError);
          }
        }
        
        // Use metadata to prefill form if available (either from session or token)
        if (metadataFromUser) {
          console.log('Using metadata for prefill:', metadataFromUser);
          
          setUserMetadata(metadataFromUser);
          
          // Prefill form
          form.setFieldsValue({
            voornaam: metadataFromUser.voornaam || '',
            achternaam: metadataFromUser.achternaam || '',
            telefoon: metadataFromUser.telefoon || '',
            // Company info
            bedrijfsnaam: metadataFromUser.bedrijfsnaam || '',
            kvk_nummer: metadataFromUser.kvk_nummer || '',
            btw_nummer: metadataFromUser.btw_nummer || '',
            adres: metadataFromUser.adres || '',
            postcode: metadataFromUser.postcode || '',
            stad: metadataFromUser.stad || '',
            land: metadataFromUser.land || 'Nederland',
            // Financial contact info
            voornaam_financieel: metadataFromUser.voornaam_financieel || '',
            achternaam_financieel: metadataFromUser.achternaam_financieel || '',
            telefoon_financieel: metadataFromUser.telefoon_financieel || '',
            email_financieel: metadataFromUser.email_financieel || ''
          });
        }
      } catch (error) {
        console.error('Session check error:', error);
      } finally {
        setInitialLoading(false);
      }
    };
    
    checkSessionAndParams();
  }, [navigate, form, searchParams]);

  const saveCompanyToDatabase = async (userData: Record<string, any>, userId: string) => {
    if (!userId || !userData.bedrijfsnaam) {
      console.log('Not creating company record: missing userId or bedrijfsnaam');
      return;
    }
    
    console.log('Creating company record for user', userId);
    
    try {
      // Create a company record in the database
      const { data, error } = await supabase
        .from('companies')
        .insert([
          {
            user_id: userId,
            bedrijfsnaam: userData.bedrijfsnaam || '',
            kvk_nummer: userData.kvk_nummer || '',
            btw_nummer: userData.btw_nummer || '',
            adres: userData.adres || '',
            postcode: userData.postcode || '',
            stad: userData.stad || '',
            land: userData.land || 'Nederland',
            voornaam_financieel: userData.voornaam_financieel || userData.voornaam || '',
            achternaam_financieel: userData.achternaam_financieel || userData.achternaam || '',
            telefoon_financieel: userData.telefoon_financieel || userData.telefoon || '',
            email_financieel: userData.email_financieel || userData.email || ''
          }
        ]);
        
      if (error) {
        console.error('Error creating company record:', error);
      } else {
        console.log('Company record created successfully');
      }
    } catch (error) {
      console.error('Exception during company creation:', error);
    }
  };

  const handleSameAsContactChange = (e: any) => {
    const checked = e.target.checked;
    setSameAsContact(checked);
    
    if (checked) {
      const values = form.getFieldsValue();
      form.setFieldsValue({
        voornaam_financieel: values.voornaam,
        achternaam_financieel: values.achternaam,
        telefoon_financieel: values.telefoon,
        email_financieel: values.email
      });
    }
  };

  const handleResendLink = async () => {
    const email = form.getFieldValue('email');
    
    if (!email) {
      message.error('Vul een e-mailadres in om de link opnieuw te verzenden');
      return;
    }
    
    try {
      setResendLoading(true);
      
      const response = await fetch('/api/admin/resend-invitation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to resend invitation');
      }
      
      message.success(`Uitnodiging opnieuw verzonden naar ${email}`);
    } catch (error: any) {
      console.error('Error resending invitation:', error);
      message.error(error.message || 'Er is een fout opgetreden bij het opnieuw versturen van de uitnodiging');
    } finally {
      setResendLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      console.log('Updating user profile and password...', values);

      if (values.password !== values.confirmPassword) {
        message.error('De wachtwoorden komen niet overeen');
        return;
      }

      // Extract the token from the URL - this is crucial for passwordless links
      // It might be in different parameters depending on the auth flow
      const token = searchParams.get('token') || 
                    searchParams.get('access_token') || 
                    searchParams.get('refresh_token');
      
      // Extract the type of auth flow
      const type = searchParams.get('type') || '';
      
      // Hash parameters might contain additional tokens
      const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      const hashToken = hashParams.get('access_token') || 
                        hashParams.get('token') || 
                        hashParams.get('refresh_token');
      
      // Also check for the special Supabase token format in the hash
      const hashValue = window.location.hash.replace('#', '');
      const hashContainsToken = hashValue && hashValue.length > 20; // Arbitrary length check for a token
      
      console.log('Found tokens:', { 
        queryToken: token, 
        hashToken,
        hashContainsToken,
        hashValue: hashContainsToken ? `${hashValue.substring(0, 10)}...` : '',
        type
      });

      // If we don't have a session, try to establish one
      if (!hasSession) {
        console.log('No session, attempting to establish one');
        
        try {
          // First try with direct sign-in if we have an email
          if (values.email) {
            console.log('Attempting to sign in with OTP');
            // Send magic link
            const { error: otpError } = await supabase.auth.signInWithOtp({
              email: values.email,
              options: {
                shouldCreateUser: false // Expecting user already exists
              }
            });
            
            if (otpError) {
              console.error('Error with OTP login:', otpError);
              // Continue to next method
            } else {
              console.log('OTP sent successfully');
              // We'll proceed with the process since we can't wait for user to check email
            }
          }
          
          // Try using the hash as a direct access token
          if (hashContainsToken) {
            console.log('Trying hash value as access token');
            const { data: userData, error: userError } = await supabase.auth.getUser(hashValue);
            
            if (!userError && userData?.user) {
              console.log('Successfully used hash as access token');
              setUserId(userData.user.id);
              
              // Try password update
              const { error: passwordError } = await supabase.auth.updateUser({
                password: values.password
              });
              
              if (passwordError) {
                console.error('Error updating password:', passwordError);
              } else {
                console.log('Password set successfully using hash access token');
              }
            }
          }
          
          // If specific token, try using it
          if (token || hashToken) {
            const usableToken = token || hashToken || '';
            console.log('Trying to exchange token for session');
            
            try {
              const { error: sessionError } = await supabase.auth.refreshSession({
                refresh_token: usableToken
              });
              
              if (sessionError) {
                console.error('Error refreshing session:', sessionError);
              } else {
                console.log('Session refreshed successfully');
                
                // Try to get the current session
                const { data: newSession } = await supabase.auth.getSession();
                if (newSession?.session?.user?.id) {
                  setUserId(newSession.session.user.id);
                }
              }
            } catch (tokenErr) {
              console.error('Exception during token exchange:', tokenErr);
            }
          }
        } catch (sessionErr) {
          console.error('General error in session establishment:', sessionErr);
        }
      }

      // Try updating the password directly
      try {
        console.log('Attempting direct password update');
        const { error: passwordError } = await supabase.auth.updateUser({
          password: values.password
        });

        if (passwordError) {
          console.error('Error in direct password update:', passwordError);
        } else {
          console.log('Password updated successfully');
        }
      } catch (passUpdateError) {
        console.error('Exception during password update:', passUpdateError);
      }

      // Build user profile data
      const userProfileData = {
        voornaam: values.voornaam || '',
        achternaam: values.achternaam || '',
        telefoon: values.telefoon || '',
        // Company info
        bedrijfsnaam: values.bedrijfsnaam || '',
        kvk_nummer: values.kvk_nummer || '',
        btw_nummer: values.btw_nummer || '',
        adres: values.adres || '',
        postcode: values.postcode || '',
        stad: values.stad || '',
        land: values.land || 'Nederland',
        // Financial contact info
        voornaam_financieel: values.voornaam_financieel || '',
        achternaam_financieel: values.achternaam_financieel || '',
        telefoon_financieel: values.telefoon_financieel || '',
        email_financieel: values.email_financieel || '',
        // Additional metadata
        acceptedTerms: true,
        passwordSet: true
      };
      
      console.log('User profile data to save:', userProfileData);
      
      // Update metadata if possible
      try {
        console.log('Attempting to update user metadata');
        await supabase.auth.updateUser({
          data: userProfileData
        });
        console.log('User metadata updated');
      } catch (metadataError) {
        console.error('Error updating metadata:', metadataError);
      }
      
      // Create company record if bedrijfsnaam is provided
      if (userProfileData.bedrijfsnaam && userId) {
        await saveCompanyToDatabase(userProfileData, userId);
      } else if (userProfileData.bedrijfsnaam) {
        console.log('Cannot create company record yet - userId not available');
        
        // Try once more to get current user
        try {
          const { data: userData } = await supabase.auth.getUser();
          if (userData?.user?.id) {
            console.log('Got userId from current user:', userData.user.id);
            await saveCompanyToDatabase(userProfileData, userData.user.id);
          }
        } catch (userError) {
          console.error('Error getting current user for company creation:', userError);
        }
      }
      
      // Success message regardless of individual step results
      message.success('Account ingesteld! U kunt nu inloggen.');
      
      // Manual sign in
      if (values.email && values.password) {
        try {
          console.log('Attempting to sign in with credentials');
          const { error: signInError } = await supabase.auth.signInWithPassword({
            email: values.email,
            password: values.password
          });
          
          if (signInError) {
            console.error('Error signing in after setup:', signInError);
          } else {
            console.log('Successfully signed in with new credentials');
            // Go directly to dashboard instead of login
            setTimeout(() => {
              navigate('/', { replace: true });
              return;
            }, 1000);
          }
        } catch (loginErr) {
          console.error('Exception during login attempt:', loginErr);
        }
      }
      
      // Navigate to login page if auto-login didn't work
      setTimeout(() => {
        navigate('/login', { replace: true, state: { passwordSet: true } });
      }, 1000);
    } catch (error: any) {
      console.error('Error setting password:', error);
      message.error(error.message || 'Er is iets misgegaan bij het instellen van uw wachtwoord.');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: '#001350',
          backgroundImage: 'linear-gradient(135deg, #001350 0%, #002080 100%)',
        }}
      >
        <div style={{ textAlign: 'center', color: 'white' }}>
          <div>Laden...</div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: '#001350',
        backgroundImage: 'linear-gradient(135deg, #001350 0%, #002080 100%)',
        padding: '20px',
      }}
    >
      <Card
        style={{
          width: '100%',
          maxWidth: 600,
          borderRadius: 8,
          padding: 24,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Logo height={48} darkMode={true} />
          <Title level={3} style={{ color: '#001350', marginBottom: 8 }}>
            Account activeren
          </Title>
          <Text style={{ display: 'block', marginBottom: 24 }}>
            Welkom bij RE-PACKED! Vul uw gegevens in en stel een wachtwoord in om uw account te activeren.
          </Text>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Tabs 
            activeKey={activeTab} 
            onChange={setActiveTab}
            type="card"
            style={{ marginBottom: 16 }}
          >
            <TabPane tab="Persoonlijke gegevens" key="1">
              <Form.Item
                name="email"
                label="E-mailadres"
              >
                <Space>
                  <Input 
                    prefix={<MailOutlined style={{ color: '#001350' }} />}
                    placeholder="E-mailadres"
                    size="large"
                    disabled={!!hasSession || !!emailFromUrl}
                    style={{ width: '100%' }}
                  />
                  {(!!hasSession || !!emailFromUrl) && (
                    <Button
                      onClick={handleResendLink}
                      loading={resendLoading}
                    >
                      Link opnieuw versturen
                    </Button>
                  )}
                </Space>
              </Form.Item>

              <Space style={{ display: 'flex', width: '100%', gap: 16 }}>
                <Form.Item
                  name="voornaam"
                  label="Voornaam"
                  rules={[{ required: true, message: 'Vul uw voornaam in' }]}
                  style={{ flex: 1 }}
                >
                  <Input
                    prefix={<UserOutlined style={{ color: '#001350' }} />}
                    placeholder="Voornaam"
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  name="achternaam"
                  label="Achternaam"
                  rules={[{ required: true, message: 'Vul uw achternaam in' }]}
                  style={{ flex: 1 }}
                >
                  <Input
                    prefix={<UserOutlined style={{ color: '#001350' }} />}
                    placeholder="Achternaam"
                    size="large"
                  />
                </Form.Item>
              </Space>

              <Form.Item
                name="telefoon"
                label="Telefoonnummer"
                rules={[{ required: true, message: 'Vul uw telefoonnummer in' }]}
              >
                <Input
                  prefix={<PhoneOutlined style={{ color: '#001350' }} />}
                  placeholder="Telefoonnummer"
                  size="large"
                />
              </Form.Item>
              
              <Divider />
              
              <Title level={5}>Financiële contactgegevens</Title>
              
              <Form.Item name="sameAsContact" valuePropName="checked" style={{ marginBottom: 16 }}>
                <Checkbox 
                  checked={sameAsContact} 
                  onChange={handleSameAsContactChange}
                >
                  Dezelfde gegevens als hierboven gebruiken
                </Checkbox>
              </Form.Item>
              
              <Space style={{ display: 'flex', width: '100%', gap: 16 }}>
                <Form.Item
                  name="voornaam_financieel"
                  label="Voornaam"
                  style={{ flex: 1 }}
                >
                  <Input
                    prefix={<UserOutlined style={{ color: '#001350' }} />}
                    placeholder="Voornaam"
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  name="achternaam_financieel"
                  label="Achternaam"
                  style={{ flex: 1 }}
                >
                  <Input
                    prefix={<UserOutlined style={{ color: '#001350' }} />}
                    placeholder="Achternaam"
                    size="large"
                  />
                </Form.Item>
              </Space>
              
              <Form.Item
                name="email_financieel"
                label="E-mailadres"
              >
                <Input
                  prefix={<MailOutlined style={{ color: '#001350' }} />}
                  placeholder="E-mailadres"
                  size="large"
                />
              </Form.Item>
              
              <Form.Item
                name="telefoon_financieel"
                label="Telefoonnummer"
              >
                <Input
                  prefix={<PhoneOutlined style={{ color: '#001350' }} />}
                  placeholder="Telefoonnummer"
                  size="large"
                />
              </Form.Item>
            </TabPane>
            
            <TabPane tab="Bedrijfsgegevens" key="2">
              <Form.Item
                name="bedrijfsnaam"
                label="Bedrijfsnaam"
              >
                <Input
                  prefix={<BankOutlined style={{ color: '#001350' }} />}
                  placeholder="Bedrijfsnaam"
                  size="large"
                />
              </Form.Item>
              
              <Space style={{ display: 'flex', width: '100%', gap: 16 }}>
                <Form.Item
                  name="kvk_nummer"
                  label="KVK-nummer"
                  style={{ flex: 1 }}
                >
                  <Input
                    prefix={<NumberOutlined style={{ color: '#001350' }} />}
                    placeholder="KVK-nummer"
                    size="large"
                  />
                </Form.Item>
                
                <Form.Item
                  name="btw_nummer"
                  label="BTW-nummer"
                  style={{ flex: 1 }}
                >
                  <Input
                    prefix={<NumberOutlined style={{ color: '#001350' }} />}
                    placeholder="BTW-nummer"
                    size="large"
                  />
                </Form.Item>
              </Space>
              
              <Space style={{ display: 'flex', width: '100%', gap: 16 }}>
                <Form.Item
                  name="adres"
                  label="Adres"
                  style={{ flex: 2 }}
                >
                  <Input
                    prefix={<HomeOutlined style={{ color: '#001350' }} />}
                    placeholder="Adres"
                    size="large"
                  />
                </Form.Item>
                
                <Form.Item
                  name="postcode"
                  label="Postcode"
                  style={{ flex: 1 }}
                >
                  <Input
                    placeholder="Postcode"
                    size="large"
                  />
                </Form.Item>
              </Space>
              
              <Form.Item
                name="stad"
                label="Stad"
              >
                <Input
                  placeholder="Stad"
                  size="large"
                />
              </Form.Item>
              
              <Form.Item name="land" hidden initialValue="Nederland">
                <Input />
              </Form.Item>
            </TabPane>
            
            <TabPane tab="Wachtwoord instellen" key="3">
              <div style={{ marginBottom: 16 }}>
                <Text style={{ display: 'block', marginBottom: 16 }}>
                  Stel een wachtwoord in waarmee u kunt inloggen op uw account.
                </Text>
              </div>
              
              <Form.Item
                name="password"
                rules={[
                  { required: true, message: 'Voer een wachtwoord in' },
                  { min: 8, message: 'Wachtwoord moet minimaal 8 tekens bevatten' }
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: '#001350' }} />}
                  placeholder="Wachtwoord"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                dependencies={['password']}
                rules={[
                  { required: true, message: 'Bevestig uw wachtwoord' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('De wachtwoorden komen niet overeen'));
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: '#001350' }} />}
                  placeholder="Bevestig wachtwoord"
                  size="large"
                />
              </Form.Item>
              
              <div style={{ marginTop: 16 }}>
                <Title level={5} style={{ color: '#001350', marginBottom: 8 }}>
                  Wachtwoordvereisten:
                </Title>
                <ul style={{ color: '#666', paddingLeft: 16 }}>
                  <li>Minimaal 8 tekens lang</li>
                  <li>Gebruik een combinatie van letters en cijfers</li>
                  <li>Gebruik ten minste één hoofdletter</li>
                </ul>
              </div>
            </TabPane>
          </Tabs>
          
          <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between' }}>
            {activeTab !== '1' && (
              <Button
                onClick={() => setActiveTab(String(Number(activeTab) - 1))}
                style={{ marginRight: 8 }}
              >
                Vorige
              </Button>
            )}
            
            <div style={{ marginLeft: 'auto' }}>
              {activeTab !== '3' ? (
                <Button
                  type="primary"
                  onClick={() => setActiveTab(String(Number(activeTab) + 1))}
                >
                  Volgende
                </Button>
              ) : (
                <Form.Item style={{ margin: 0 }}>
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    loading={loading}
                    size="large"
                    style={{
                      backgroundColor: '#FF5d00',
                      borderColor: '#FF5d00',
                      height: '45px',
                      fontSize: '16px',
                    }}
                  >
                    Account activeren
                  </Button>
                </Form.Item>
              )}
            </div>
          </div>
        </Form>
      </Card>
    </div>
  );
} 